import React, { useState, useContext } from "react";
import LoaderAnimation from "./LoaderAnimation";
import { useNavigationEvent } from "navigation-react";
import mainLogo from "./images/mainlogo.png";
import SecureLS from "secure-ls";
import { RoleContext } from "./App";

const ls = new SecureLS({ encodingType: "aes" });

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [cookieWarning, setCookieWarning] = useState("");
  const { stateNavigator } = useNavigationEvent();
  const { setRoles } = useContext(RoleContext);

  const [isButtonHovered, setIsButtonHovered] = useState(false);
  const [isButtonActive, setIsButtonActive] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  // Add error handling for the image
  const [logoError, setLogoError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Prevent double submission on touch
    if (loading) return;

    if (!username.trim() || !password.trim()) {
      alert("Please enter your credentials");
      return;
    }

    setLoading(true);
    setCookieWarning("");

    try {
      // Step 1: Clear any existing session
      console.log("🔄 Clearing any existing session...");
      try {
        const logoutResponse = await fetch(
          `${process.env.REACT_APP_BASE_URL}/user/logout`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        console.log("📡 Logout Response Status:", logoutResponse.status);
      } catch (logoutError) {
        // Ignore logout errors - user might not be logged in
        console.log(
          "ℹ️ No active session to clear or logout failed:",
          logoutError.message,
        );
      }

      // Step 2: Wait a moment for logout to process
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Step 3: Perform login
      const loginUrl = `${process.env.REACT_APP_LOGIN_URL}`;
      const loginResponse = await fetch(loginUrl, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ name: username, pass: password }),
      });

      if (!loginResponse.ok) {
        const errJson = await loginResponse.json().catch(() => ({}));
        alert("Login failed. Please check your credentials.", errJson.message);
        setLoading(false);
        return;
      }

      const loginData = await loginResponse.json();

      // ✅ Get roles from login response IMMEDIATELY
      const userRoles = loginData.current_user.roles || [];

      // Step 4: Save to SecureLS
      ls.set("username", loginData.current_user.name);
      ls.set("currentuserid", loginData.current_user.uid);
      ls.set("currentuserrole", userRoles);
      ls.set("userlogout_token", loginData.logout_token);

      // Step 5: Update context with roles
      setRoles(userRoles);

      // Step 6: Get CSRF token
      const csrfRes = await fetch(
        `${process.env.REACT_APP_BASE_URL}/session/token`,
        {
          method: "GET",
          credentials: "include",
          headers: { Accept: "application/json" },
        },
      );
      const csrfToken = await csrfRes.text();
      ls.set("usercsrf_token", csrfToken);

      // Step 7: Get permissions
      const permUrl = `${process.env.REACT_APP_BASE_URL}/api/user/permissions`;
      const permRes = await fetch(permUrl, {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
      });

      if (!permRes.ok) {
        const text = await permRes.text();
        alert(`Failed to load permissions (status ${permRes.status}): ${text}`);
        return;
      }

      const permissions = await permRes.json();
      ls.set("permissions", permissions);

      alert("Login successful");

      // ✅ Check roles using the variable (NOT the state)
      const isAdmin = userRoles.some(
        (role) => role === "administrator" || role === "admin",
      );
      const isCashier = userRoles.some((role) => role === "cashier");

      console.log("👤 User Roles:", userRoles);
      console.log("✅ Is Admin:", isAdmin);
      console.log("✅ Is Cashier:", isCashier);

      // ✅ Redirect based on role using the variable
      if (isAdmin) {
        stateNavigator.navigate("dashboardcontent", { replace: true });
      } else if (isCashier) {
        stateNavigator.navigate("orderregister", { replace: true });
      } else {
        // Default redirect for other roles
        stateNavigator.navigate("dashboardcontent", { replace: true });
      }
    } catch (error) {
      console.error("❌ Login Error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // if (loading) return <LoaderAnimation />;

  const styles = {
    container: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #f7f7f9 0%, #dfdfdf 100%)",
      padding: "20px",
      fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
      // Touch-specific container fix
      touchAction: "pan-y",
      WebkitOverflowScrolling: "touch",
    },
    card: {
      background: "#ffffff",
      borderRadius: "24px",
      padding: "48px 40px",
      width: "100%",
      maxWidth: "480px",
      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
      animation: "slideUp 0.5s ease-out",
    },
    logoContainer: {
      textAlign: "center",
      marginBottom: "32px",
    },
    logoImage: {
      width: "120px",
      height: "120px",
      objectFit: "contain",
      marginBottom: "12px",
    },
    logoTitle: {
      fontSize: "24px",
      fontWeight: 700,
      color: "#2d3748",
      letterSpacing: "2px",
      margin: 0,
    },
    logoSubtitle: {
      fontSize: "14px",
      color: "#718096",
      marginTop: "4px",
      fontWeight: 400,
    },
    form: {
      display: "flex",
      flexDirection: "column",
      gap: "20px",
    },
    welcomeText: {
      fontSize: "16px",
      color: "#4a5568",
      textAlign: "center",
      marginBottom: "8px",
    },
    inputGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    },
    label: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#2d3748",
      marginLeft: "4px",
    },
    input: {
      padding: "16px 18px",
      fontSize: "16px", // Prevents iOS zoom
      border: "2px solid #e2e8f0",
      borderRadius: "12px",
      transition: "all 0.3s ease",
      backgroundColor: "#f7fafc",
      outline: "none",
      minHeight: "56px",
      // Touch-specific fixes
      WebkitUserSelect: "text",
      userSelect: "text",
      WebkitTouchCallout: "none",
    },
    inputFocus: {
      borderColor: "#667eea",
      backgroundColor: "#ffffff",
      boxShadow: "0 0 0 3px rgba(102, 126, 234, 0.2)",
    },
    loginButton: {
      padding: "18px",
      fontSize: "18px",
      fontWeight: 600,
      color: "#ffffff",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      border: "none",
      borderRadius: "12px",
      cursor: "pointer",
      transition: "all 0.3s ease",
      minHeight: "60px",
      marginTop: "8px",
      WebkitTapHighlightColor: "transparent",
      touchAction: "manipulation",
    },
    loginButtonHover: {
      transform: "translateY(-2px)",
      boxShadow: "0 8px 25px rgba(102, 126, 234, 0.4)",
    },
    loginButtonActive: {
      transform: "scale(0.98)",
    },
    loginButtonDisabled: {
      opacity: 0.7,
      cursor: "not-allowed",
      transform: "none",
    },
    footer: {
      marginTop: "32px",
      textAlign: "center",
      fontSize: "16px",
      color: "#a0aec0",
      borderTop: "1px solid #e2e8f0",
      paddingTop: "20px",
    },
    footerPhone: {
      color: "#718096",
      fontSize: "16px",
      fontWeight: 500,
    },
    cookieWarning: {
      backgroundColor: "#fed7d7",
      color: "#c53030",
      padding: "12px 16px",
      borderRadius: "10px",
      fontSize: "14px",
      marginBottom: "8px",
    },
  };

  const animations = `
    @keyframes slideUp {
      from {
        opacity: 0;
        transform: translateY(30px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    
    @media (max-width: 480px) {
      .login-card-custom {
        padding: 32px 24px !important;
      }
      .login-logo-image {
        width: 80px !important;
        height: 80px !important;
      }
      .login-logo-title {
        font-size: 20px !important;
      }
      .login-input {
        min-height: 48px !important;
        padding: 14px 16px !important;
        font-size: 15px !important;
      }
      .login-button {
        min-height: 52px !important;
        padding: 16px !important;
        font-size: 16px !important;
      }
    }

    @media (min-width: 768px) and (max-width: 1024px) {
      .login-card-custom {
        max-width: 420px !important;
        padding: 40px 36px !important;
      }
    }

    @media (max-height: 600px) and (orientation: landscape) {
      .login-card-custom {
        padding: 24px 32px !important;
        max-width: 400px !important;
      }
      .login-logo-container {
        margin-bottom: 16px !important;
      }
      .login-logo-image {
        width: 70px !important;
        height: 70px !important;
        margin-bottom: 6px !important;
      }
      .login-logo-title {
        font-size: 18px !important;
      }
      .login-form {
        gap: 12px !important;
      }
      .login-input {
        min-height: 40px !important;
        padding: 10px 14px !important;
        font-size: 14px !important;
      }
      .login-button {
        min-height: 44px !important;
        padding: 12px !important;
        font-size: 15px !important;
        margin-top: 4px !important;
      }
      .login-welcome-text {
        display: none !important;
      }
    }

    /* FIXED: Touch-specific media query */
    @media (hover: none) and (pointer: coarse) {
      .login-input {
        font-size: 16px !important; /* Prevents iOS zoom */
        min-height: 48px !important;
        padding: 14px 16px !important;
        -webkit-appearance: none !important;
        appearance: none !important;
      }
      .login-button {
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
      }
      .login-button:active {
        transform: scale(0.96) !important;
        transition: transform 0.1s ease;
      }
      /* Ensure inputs are focusable */
      .login-input:focus {
        border-color: #667eea !important;
        background-color: #ffffff !important;
        outline: none !important;
      }
      /* Prevent zoom on focus */
      .login-input:focus {
        font-size: 16px !important;
      }
    }
  `;

  return (
    <>
      <style>{animations}</style>
      <div style={styles.container}>
        <div className="login-card-custom" style={styles.card}>
          <div className="login-logo-container" style={styles.logoContainer}>
            {!logoError ? (
              <img
                src={mainLogo}
                alt="POS System"
                className="login-logo-image"
                style={styles.logoImage}
                onError={() => setLogoError(true)}
              />
            ) : (
              // Fallback if image fails to load
              <div
                style={{
                  width: "120px",
                  height: "120px",
                  backgroundColor: "#667eea",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  fontSize: "48px",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                POS
              </div>
            )}
            <h2 className="login-logo-title" style={styles.logoTitle}>
              POINT OF SALE
            </h2>
          </div>

          {cookieWarning && (
            <div style={styles.cookieWarning}>⚠️ {cookieWarning}</div>
          )}

          <form
            onSubmit={handleSubmit}
            className="login-form"
            style={styles.form}
          >
            <div style={styles.inputGroup}>
              <label htmlFor="username" style={styles.label}>
                Username
              </label>
              <input
                type="text"
                id="username"
                className="login-input"
                placeholder="Enter username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onFocus={() => setFocusedField("username")}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...styles.input,
                  ...(focusedField === "username" ? styles.inputFocus : {}),
                }}
                required
                autoComplete="username"
                inputMode="text"
                enterKeyHint="next"
                // Touch-specific attributes
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                // Force focus on touch
                onTouchStart={(e) => {
                  e.stopPropagation();
                  setTimeout(() => e.target.focus(), 10);
                }}
              />
            </div>

            <div style={styles.inputGroup}>
              <label htmlFor="password" style={styles.label}>
                Password
              </label>
              <input
                type="password"
                id="password"
                className="login-input"
                placeholder="Enter password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocusedField("password")}
                onBlur={() => setFocusedField(null)}
                style={{
                  ...styles.input,
                  ...(focusedField === "password" ? styles.inputFocus : {}),
                }}
                required
                autoComplete="current-password"
                enterKeyHint="done"
                // Touch-specific attributes
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                // Force focus on touch
                onTouchStart={(e) => {
                  e.stopPropagation();
                  setTimeout(() => e.target.focus(), 10);
                }}
              />
            </div>

            <button
              type="submit"
              className="login-button"
              style={{
                ...styles.loginButton,
                ...(isButtonHovered && !loading ? styles.loginButtonHover : {}),
                ...(isButtonActive && !loading ? styles.loginButtonActive : {}),
                ...(loading ? styles.loginButtonDisabled : {}),
              }}
              disabled={loading}
              onMouseEnter={() => setIsButtonHovered(true)}
              onMouseLeave={() => setIsButtonHovered(false)}
              onMouseDown={() => setIsButtonActive(true)}
              onMouseUp={() => setIsButtonActive(false)}
              onTouchStart={() => setIsButtonActive(true)}
              onTouchEnd={() => setIsButtonActive(false)}
            >
              {loading ? "Signing in..." : "Login"}
            </button>
          </form>

          <footer style={styles.footer}>
            <p>© {new Date().getFullYear()} Reactive System Developers</p>
            <p style={styles.footerPhone}>0257335982</p>
          </footer>
        </div>
      </div>
    </>
  );
};

export default Login;
